import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Archive,
  Calendar,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  StickyNote,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { LinkText } from '@/components/LinkText';
import {
  MigratedCrudHistory,
  formatMigratedDateTime,
} from '@/components/MigratedCrudHistory';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
  personalNotesApi,
  type PersonalNote,
  type SavePersonalNoteDto,
} from '@/api/personal-notes';

const FIELD_LABELS: Record<string, string> = {
  text: 'Текст',
  isArchived: 'В архиве',
  archivedAt: 'Архивировано',
};

const HIDDEN_HISTORY_FIELDS = [
  'ownerId',
  'legacyId',
  'legacySobid',
  'legacyCreator',
  'importedAt',
  'isArchived',
  'archivedAt',
];

function formatDate(value?: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    return response?.data?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function PersonalNoteCard({
  note,
  onOpen,
}: {
  note: PersonalNote;
  onOpen: (note: PersonalNote) => void;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(note);
    }
  };

  return (
    <article
      className="rounded-lg border border-border bg-card p-3 active:bg-accent"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(note)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="line-clamp-3 whitespace-pre-wrap break-words text-sm">
            {note.text}
          </div>
          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {formatDate(note.updatedAt || note.createdAt)}
          </div>
        </div>
        {note.isArchived && <Archive className="h-4 w-4 shrink-0 text-muted-foreground" />}
      </div>
    </article>
  );
}

function PersonalNoteHistory({
  note,
  open,
}: {
  note: PersonalNote;
  open: boolean;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['personal-note-history', note.id],
    queryFn: () => personalNotesApi.getHistory(note.id),
    enabled: open,
  });

  return (
    <MigratedCrudHistory
      auditItems={data?.items || []}
      total={data?.total}
      isLoading={isLoading}
      error={error}
      fieldLabels={FIELD_LABELS}
      hiddenFields={HIDDEN_HISTORY_FIELDS}
    />
  );
}

function PersonalNoteDetailSheet({
  note,
  open,
  onOpenChange,
  onEdit,
  onArchive,
}: {
  note: PersonalNote | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (note: PersonalNote) => void;
  onArchive: (note: PersonalNote) => void;
}) {
  if (!note) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader className="pr-8 text-left">
          <SheetTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Заметка
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex gap-2">
            <Button className="flex-1" onClick={() => onEdit(note)}>
              <Pencil className="h-4 w-4" />
              Редактировать
            </Button>
            {!note.isArchived && (
              <Button variant="outline" onClick={() => onArchive(note)}>
                <Archive className="h-4 w-4" />
                В архив
              </Button>
            )}
          </div>

          <div className="rounded-lg border border-border p-3">
            <div className="mb-1 text-xs font-medium uppercase tracking-normal text-muted-foreground">
              Текст
            </div>
            <div className="whitespace-pre-wrap break-words text-sm">
              <LinkText>{note.text}</LinkText>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3 text-xs">
            <span className="text-muted-foreground">Создано</span>
            <span className="text-right">{formatMigratedDateTime(note.createdAt)}</span>
            <span className="text-muted-foreground">Изменено</span>
            <span className="text-right">{formatMigratedDateTime(note.updatedAt)}</span>
            {note.importedAt && (
              <>
                <span className="text-muted-foreground">Импортировано</span>
                <span className="text-right">{formatMigratedDateTime(note.importedAt)}</span>
              </>
            )}
          </div>

          <PersonalNoteHistory note={note} open={open} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PersonalNoteFormSheet({
  note,
  open,
  onOpenChange,
  onSaved,
}: {
  note: PersonalNote | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (note: PersonalNote) => void;
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');

  useEffect(() => {
    if (!open) return;
    setText(note?.text || '');
  }, [note, open]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: SavePersonalNoteDto = { text: text.trim() };
      return note ? personalNotesApi.update(note.id, body) : personalNotesApi.create(body);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['personal-notes'] });
      queryClient.invalidateQueries({ queryKey: ['personal-note-history', saved.id] });
      toast.success('Заметка сохранена');
      onSaved(saved);
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error, 'Ошибка сохранения')),
  });

  const canSubmit = text.trim().length > 0 && !saveMutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader className="pr-8 text-left">
          <SheetTitle>{note ? 'Редактировать заметку' : 'Новая заметка'}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Текст</label>
            <Textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              rows={8}
              autoFocus
            />
          </div>

          <div className="flex gap-2 border-t border-border pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={saveMutation.isPending}
            >
              Отмена
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={() => saveMutation.mutate()}
              disabled={!canSubmit}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Сохранить
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function PersonalNotesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedNote, setSelectedNote] = useState<PersonalNote | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formNote, setFormNote] = useState<PersonalNote | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['personal-notes', search, showArchived],
    queryFn: () => personalNotesApi.getAll({ search, archived: showArchived }),
  });

  const notes = useMemo(() => data || [], [data]);

  const archiveMutation = useMutation({
    mutationFn: (note: PersonalNote) => personalNotesApi.archive(note.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal-notes'] });
      toast.success('Заметка перенесена в архив');
      setDetailOpen(false);
      setSelectedNote(null);
    },
    onError: (error) => toast.error(errorMessage(error, 'Ошибка архивации')),
  });

  const openDetail = (note: PersonalNote) => {
    setSelectedNote(note);
    setDetailOpen(true);
  };

  const openCreate = () => {
    setFormNote(null);
    setFormOpen(true);
  };

  const openEdit = (note: PersonalNote) => {
    setDetailOpen(false);
    setFormNote(note);
    setFormOpen(true);
  };

  const handleSaved = (note: PersonalNote) => {
    setSelectedNote(note);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Мои заметки"
        showBack
        rightContent={
          <Button size="icon" aria-label="Добавить" onClick={openCreate}>
            <Plus className="h-5 w-5" />
          </Button>
        }
      />

      <main className="p-4">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Поиск"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-10 pr-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mb-4 grid grid-cols-2 rounded-lg border border-border p-1">
          <Button
            variant={!showArchived ? 'default' : 'ghost'}
            onClick={() => setShowArchived(false)}
          >
            Активные
          </Button>
          <Button
            variant={showArchived ? 'default' : 'ghost'}
            onClick={() => setShowArchived(true)}
          >
            Архив
          </Button>
        </div>

        {!isLoading && !error && (
          <p className="mb-3 text-sm text-muted-foreground">Всего записей: {notes.length}</p>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="mb-4 h-12 w-12 text-destructive" />
            <p className="text-muted-foreground">Ошибка загрузки</p>
          </div>
        )}

        {!isLoading && !error && notes.length > 0 && (
          <div className="space-y-2">
            {notes.map((note) => (
              <PersonalNoteCard key={note.id} note={note} onOpen={openDetail} />
            ))}
          </div>
        )}

        {!isLoading && !error && notes.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            {showArchived ? (
              <Archive className="mb-4 h-12 w-12 text-muted-foreground" />
            ) : (
              <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
            )}
            <p className="text-muted-foreground">Заметок нет</p>
          </div>
        )}
      </main>

      <PersonalNoteDetailSheet
        note={selectedNote}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={openEdit}
        onArchive={(note) => archiveMutation.mutate(note)}
      />

      <PersonalNoteFormSheet
        note={formNote}
        open={formOpen}
        onOpenChange={setFormOpen}
        onSaved={handleSaved}
      />
    </div>
  );
}
